import React, { useCallback, useEffect, useState } from 'react';
import Icon from './Icon';
import { useTranslation } from 'react-i18next';
import { apiErrorText, apiRequest } from '../../utils';
import './MailSignOff.css';

/* How this company's automatic emails close.

   ★ It exists because the sign-off used to hang on the BUSINESS UNIT, and one business unit
   carries dozens of customers — 43 companies organise events under Hercules Fabrica Belgium
   alone. Every one of them closed their mail as ours, on a mail whose body already printed
   their own name, and only the back office could change it.

   ★ The BU's values are shown, not hidden. A blank box cannot say whether nothing is set or
   nothing loaded; "your emails close with The Bravos Test Team" says what the customer's people
   are reading today and what clearing the field goes back to.

   ★ The address is the REPLY address, and the hint says so out loud. It is the one field here
   with a consequence beyond the words on the page: replies to every event mail land there, so a
   customer typing a mailbox nobody watches is the failure worth naming up front.

   ★ Name and address travel together. The server refuses an address without a name, because a
   sign-off resolves as a pair — an address alone would print OUR name over THEIR mailbox. */

const MailSignOff = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const adopt = useCallback((res) => {
    setData(res);
    setName(res.name || '');
    setEmail(res.email || '');
  }, []);

  const load = useCallback(() => {
    apiRequest('GET', '/company/comms/signature')
      .then(adopt)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [adopt, t]);

  useEffect(() => { load(); }, [load]);

  const save = (body, done) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('PUT', '/company/comms/signature', body)
      .then((res) => { adopt(res); setNotice(done); })
      .catch((err) => setError(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return error ? <p className="bravos-cal__notice">{error}</p>
      : <span className="auth__spinner" />;
  }

  const mine = data.source === 'company';
  // What a mail closes with right now — the customer's own answer, or the one they inherit.
  const shownName = mine ? data.name : data.bu_name;
  const shownEmail = mine ? data.email : data.bu_email;
  const dirty = name !== (data.name || '') || email !== (data.email || '');

  return (
    <div className="msig">
      <p className="bravos-q__hint">{t('signOffNote')}</p>

      {/* What they are sending today, before any editing. Rendered as the sign-off itself
          rather than described, because that is the thing being decided. */}
      <p className="bravos-cal__label">{t('signOffToday')}</p>
      <div className="msig-preview">
        <span className="msig-preview__lead">{t('signOffLead')}</span>
        <span className="msig-preview__name">{shownName || t('signOffNeutral')}</span>
        {shownEmail && <span className="msig-preview__email">{shownEmail}</span>}
      </div>
      {!mine && (
        <p className="ce-note">{t('signOffUsingDefault')}</p>
      )}

      <label className="msig-label" htmlFor="msig-name">{t('signOffName')}</label>
      <input id="msig-name" type="text" className="gl-search" maxLength={120}
             value={name} placeholder={data.bu_name || t('signOffNeutral')}
             onChange={(e) => setName(e.target.value)} />
      <p className="ce-note">{t('signOffNameHint')}</p>

      <label className="msig-label" htmlFor="msig-email">{t('signOffEmail')}</label>
      <input id="msig-email" type="email" className="gl-search" maxLength={120}
             value={email} placeholder={data.bu_email || ''}
             onChange={(e) => setEmail(e.target.value)} />
      {/* The consequence, not the format. Everything else on this screen is words in a mail;
          this one decides where a person's reply actually goes. */}
      <p className="ce-note msig-warn">
        <Icon name="reply" /> {t('signOffEmailHint')}
      </p>

      <div className="bravos-form__row mt-2">
        <button type="button" className="bravos-submit" disabled={busy || !dirty}
                onClick={() => save({ name, email }, t('signOffSaved'))}>
          {t('save')}
        </button>
        {dirty && (
          <button type="button" className="bravos-submit bravos-submit--ghost" disabled={busy}
                  onClick={() => { setName(data.name || ''); setEmail(data.email || ''); }}>
            {t('cancel')}
          </button>
        )}
        {/* Only offered once there is something of their own to clear — and it says what it
            goes back TO, because "clear" alone reads as "send unsigned mail". */}
        {mine && !dirty && (
          <button type="button" className="bravos-copy" disabled={busy}
                  onClick={() => save({ name: '', email: '' }, t('signOffCleared'))}>
            <Icon name="refresh" /> {t('signOffUseDefault')}
          </button>
        )}
      </div>

      {notice && <p className="bravos-cal__notice">{notice}</p>}
      {error && <p className="bravos-cal__notice">{error}</p>}
    </div>
  );
};

export default MailSignOff;
